import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import { getCalendarGlobalOverrides } from './calendarStyles';

// TAS Ski Team Color Palette
const colors = {
    primary: {
        main: '#1a3a5c',      // Deep navy blue
        light: '#2d5a87',     // Lighter navy
        dark: '#0d1b2a',      // Very dark blue
        contrastText: '#ffffff',
    },
    secondary: {
        main: '#e31837',      // Ski racing red
        light: '#ff8a95',     // Light coral red (5.23:1 contrast on #1a3a5c navy)
        dark: '#a01025',      // Darker red
        contrastText: '#ffffff',
    },
    snow: {
        main: '#f8fafc',      // Snow white
        light: '#ffffff',
        dark: '#e2e8f0',
    },
    ice: {
        main: '#94a3b8',      // Ice gray
        light: '#cbd5e1',
        dark: '#475569',
    },
    mountain: {
        main: '#1e293b',      // Dark mountain
        light: '#334155',
        dark: '#0f172a',
    }
};

// Brand surface for white-foreground heroes, CTA bands, icon tiles and
// table headers. Intentionally mode-invariant: dark-mode primary.main
// (#90caf9) is a light tint that fails WCAG AA under white text
// (1.74:1). Navy #1a3a5c yields ~11.7:1 with white and 5.23:1 with
// secondary.light (#ff8a95) overlines in both modes (Issue #855).
const heroSurface = {
    main: colors.primary.main,
    contrastText: '#ffffff',
};

// Brand red surface for white-foreground badges, CTA bands and icon
// tiles. Mode-invariant for the same reason as `hero`: dark-mode
// secondary.main (#f48fb1) yields 2.23:1 under white text; #e31837
// yields 4.63:1 (Issue #855).
const accentSurface = {
    main: colors.secondary.main,
    contrastText: '#ffffff',
};

const darkPalette = {
    ...colors,
    mode: 'dark',
    primary: {
        main: '#90caf9',
        light: '#e3f2fd',
        dark: '#42a5f5',
        contrastText: '#0B131E',
    },
    secondary: {
        main: '#f48fb1',
        light: '#ff8a95',
        dark: '#c2185b',
        contrastText: '#0B131E',
    },
    hero: heroSurface,
    accent: accentSurface,
    error: { main: '#f44336', contrastText: '#0B131E' },
    warning: {
        main: '#ed6c02',
        contrastText: '#0B131E',
    },
    background: {
        default: '#0B131E',
        paper: '#111C2E',
        dark: '#070C13',
        snow: '#162238',
    },
    text: {
        primary: '#F8FAFC',
        secondary: '#94A3B8',
        disabled: '#64748B',
    },
    action: {
        disabled: '#576579',
        disabledBackground: '#1e293b',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
};

const lightPalette = {
    ...colors,
    mode: 'light',
    primary: colors.primary,
    secondary: colors.secondary,
    hero: heroSurface,
    accent: accentSurface,
    warning: {
        main: '#ed6c02',
        contrastText: '#0f172a',
    },
    background: {
        default: '#ffffff',
        paper: '#ffffff',
        dark: colors.mountain.main,
        snow: colors.snow.main,
    },
    text: {
        primary: colors.mountain.main,
        secondary: colors.ice.dark,
        disabled: '#576579',
    },
    action: {
        disabled: '#576579',
        disabledBackground: '#e2e8f0',
    },
    divider: colors.ice.light,
};

const themeTypography = {
    fontFamily: '"Open Sans", "Helvetica Neue", Arial, sans-serif',
    h1: {
        fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 800,
        fontSize: '2.25rem',
        letterSpacing: '-0.02em',
        lineHeight: 1.15,
    },
    h2: {
        fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 700,
        fontSize: '1.85rem',
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
    },
    h3: {
        fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 700,
        fontSize: '1.45rem',
        lineHeight: 1.3,
    },
    h4: {
        fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 600,
        fontSize: '1.2rem',
        lineHeight: 1.4,
    },
    h5: {
        fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 600,
        fontSize: '1.05rem',
        lineHeight: 1.4,
    },
    h6: {
        fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 600,
        fontSize: '0.95rem',
        lineHeight: 1.5,
    },
    subtitle1: {
        fontWeight: 500,
        fontSize: '0.925rem',
        lineHeight: 1.6,
    },
    subtitle2: {
        fontWeight: 600,
        fontSize: '0.825rem',
        lineHeight: 1.6,
    },
    body1: {
        fontSize: '0.925rem',
        lineHeight: 1.65,
    },
    body2: {
        fontSize: '0.825rem',
        lineHeight: 1.6,
    },
    button: {
        fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'none',
        letterSpacing: '0.02em',
    },
    overline: {
        fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
        fontWeight: 700,
        fontSize: '0.75rem',
        letterSpacing: '0.15em',
    },
};

const themeShadows = [
    'none',
    '0 1px 3px rgba(0,0,0,0.08)',
    '0 2px 6px rgba(0,0,0,0.1)',
    '0 4px 12px rgba(0,0,0,0.12)',
    '0 6px 16px rgba(0,0,0,0.14)',
    '0 8px 24px rgba(0,0,0,0.16)',
    '0 12px 32px rgba(0,0,0,0.18)',
    '0 16px 40px rgba(0,0,0,0.2)',
    '0 20px 48px rgba(0,0,0,0.22)',
    '0 24px 56px rgba(0,0,0,0.24)',
    '0 28px 64px rgba(0,0,0,0.26)',
    '0 32px 72px rgba(0,0,0,0.28)',
    '0 36px 80px rgba(0,0,0,0.3)',
    '0 40px 88px rgba(0,0,0,0.32)',
    '0 44px 96px rgba(0,0,0,0.34)',
    '0 48px 104px rgba(0,0,0,0.36)',
    '0 52px 112px rgba(0,0,0,0.38)',
    '0 56px 120px rgba(0,0,0,0.4)',
    '0 60px 128px rgba(0,0,0,0.42)',
    '0 64px 136px rgba(0,0,0,0.44)',
    '0 68px 144px rgba(0,0,0,0.46)',
    '0 72px 152px rgba(0,0,0,0.48)',
    '0 76px 160px rgba(0,0,0,0.5)',
    '0 80px 168px rgba(0,0,0,0.52)',
    '0 84px 176px rgba(0,0,0,0.54)',
];

const getButtonOverrides = (isDark) => ({
    root: {
        borderRadius: 8,
        padding: '8px 18px',
        fontSize: '0.875rem',
        boxShadow: 'none',
        textTransform: 'none',
        '&:hover': {
            boxShadow: isDark
                ? '0 4px 12px rgba(0,0,0,0.5)'
                : '0 4px 12px rgba(0,0,0,0.12)',
            transform: 'translateY(-1px)',
        },
        '&.Mui-disabled': {
            backgroundImage: 'none',
        },
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    sizeSmall: {
        padding: '5px 12px',
        fontSize: '0.8125rem',
        borderRadius: 6,
    },
    sizeMedium: {
        padding: '8px 18px',
        fontSize: '0.875rem',
        borderRadius: 8,
    },
    sizeLarge: {
        padding: '12px 24px',
        fontSize: '0.95rem',
        borderRadius: 10,
    },
    containedPrimary: {
        background: isDark
            ? '#90caf9'
            : `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%)`,
        '&:hover': {
            background: isDark
                ? '#42a5f5'
                : `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
        },
        '&.Mui-disabled': {
            background: 'none',
            backgroundColor: isDark ? '#1e293b' : '#e2e8f0',
            color: '#576579',
        },
    },
    containedSecondary: {
        background: isDark
            ? '#f48fb1'
            : `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.light} 100%)`,
        '&:hover': {
            background: isDark
                ? '#c2185b'
                : `linear-gradient(135deg, ${colors.secondary.light} 0%, ${colors.secondary.main} 100%)`,
        },
        '&.Mui-disabled': {
            background: 'none',
            backgroundColor: isDark ? '#1e293b' : '#e2e8f0',
            color: '#576579',
        },
    },
    outlined: {
        borderWidth: 1.5,
        '&:hover': {
            borderWidth: 1.5,
        },
        '&.Mui-disabled': {
            borderColor: '#cbd5e1',
            color: '#576579',
        },
    },
});

const getCardOverrides = (isDark) => ({
    root: {
        borderRadius: 16,
        boxShadow: isDark
            ? '0 4px 20px rgba(0,0,0,0.4)'
            : '0 4px 20px rgba(0,0,0,0.08)',
        border: isDark
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(226, 232, 240, 0.8)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            boxShadow: isDark
                ? '0 8px 30px rgba(0,0,0,0.6)'
                : '0 8px 30px rgba(0,0,0,0.12)',
            transform: 'translateY(-4px)',
        },
    },
});

const getChipOverrides = () => ({
    root: {
        fontWeight: 500,
        height: 'auto',
        maxWidth: '100%',
    },
    label: {
        whiteSpace: 'normal',
        textOverflow: 'clip',
        wordBreak: 'break-word',
    },
    sizeSmall: {
        minHeight: '24px',
        paddingTop: '2px',
        paddingBottom: '2px',
    },
    sizeMedium: {
        minHeight: '32px',
        paddingTop: '4px',
        paddingBottom: '4px',
    },
});

const getInputOverrides = (isDark) => ({
    MuiInputLabel: {
        styleOverrides: {
            outlined: {
                '&.MuiInputLabel-shrink': {
                    backgroundColor: isDark ? '#1e293b' : '#ffffff',
                    padding: '0 4px',
                    marginLeft: '-2px',
                },
            },
        },
    },
    MuiInputBase: {
        styleOverrides: {
            root: {
                '@media (max-width: 600px)': {
                    fontSize: '16px',
                },
            },
            input: {
                '@media (max-width: 600px)': {
                    fontSize: '16px !important',
                },
            },
            inputSizeSmall: {
                '@media (max-width: 600px)': {
                    fontSize: '16px !important',
                },
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: 12,
                '@media (max-width: 600px)': {
                    fontSize: '16px',
                },
            },
            input: {
                '@media (max-width: 600px)': {
                    fontSize: '16px !important',
                },
            },
            inputSizeSmall: {
                '@media (max-width: 600px)': {
                    fontSize: '16px !important',
                },
            },
            notchedOutline: {
                '& legend': {
                    visibility: 'visible !important',
                },
            },
        },
    },
});

const getComponentOverrides = (isDark) => ({
    MuiCssBaseline: {
        styleOverrides: {
            html: {
                scrollBehavior: 'smooth',
            },
            body: {
                overflowX: 'hidden',
            },
            'button, a, input, select, textarea': {
                touchAction: 'manipulation',
            },
            '@media (max-width: 600px)': {
                'input, textarea, select': {
                    fontSize: '16px !important',
                },
            },
            ...getCalendarGlobalOverrides(isDark),
        },
    },
    MuiButton: {
        styleOverrides: getButtonOverrides(isDark),
    },
    MuiCard: {
        styleOverrides: getCardOverrides(isDark),
    },
    MuiTableHead: {
        styleOverrides: {
            root: {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : '#f8fafc',
                '& .MuiTableCell-head': {
                    color: isDark ? '#F8FAFC' : '#1e293b',
                    fontWeight: 700,
                },
            },
        },
    },
    MuiAppBar: {
        styleOverrides: {
            root: {
                boxShadow: 'none',
                backdropFilter: 'blur(10px)',
                backgroundColor: isDark ? 'rgba(17, 28, 46, 0.9)' : 'rgba(255, 255, 255, 0.9)',
            },
        },
    },
    MuiToolbar: {
        styleOverrides: {
            root: {
                minHeight: 80,
                '@media (min-width: 600px)': {
                    minHeight: 80,
                },
            },
        },
    },
    MuiLink: {
        styleOverrides: {
            root: {
                textDecoration: 'none',
                transition: 'color 0.2s ease',
                '&:hover': {
                    textDecoration: 'none',
                },
            },
        },
    },
    MuiChip: {
        styleOverrides: getChipOverrides(),
    },
    ...getInputOverrides(isDark),
    MuiTextField: {
        styleOverrides: {
            root: {
                '& .MuiOutlinedInput-root': {
                    borderRadius: 12,
                },
                '@media (max-width: 600px)': {
                    '& .MuiInputBase-input': {
                        fontSize: '16px !important',
                    },
                },
            },
        },
    },
});

export const getAppTheme = (mode = 'light') => {
    const isDark = mode === 'dark';

    const baseTheme = createTheme({
        palette: isDark ? darkPalette : lightPalette,
        typography: themeTypography,
        shape: {
            borderRadius: 8,
        },
        shadows: themeShadows,
        components: getComponentOverrides(isDark),
    });

    return responsiveFontSizes(baseTheme, {
        breakpoints: ['sm', 'md', 'lg'],
        factor: 3,
    });
};

const defaultTheme = getAppTheme('light');
export default defaultTheme;
